import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, Grid,
  InputAdornment, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  DataGridPremium, GridActionsCellItem, GridToolbarColumnsButton,
  GridToolbarExport, GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getTenantIdFromURL from './utils/getTenantIdFromURL';
import requestStatusService from './api/tenantApi';

const STATUS_OPTIONS = ['Sent', 'Processing', 'Approved', 'Rejected'];
const NOTIFIABLE_STATUSES = new Set(['Processing', 'Approved', 'Rejected']);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_STYLES = {
  Approved: { color: '#137A4D', backgroundColor: '#E7F7EF' },
  Processing: { color: '#1459C7', backgroundColor: '#EAF2FF' },
  Rejected: { color: '#B42318', backgroundColor: '#FDECEC' },
  Sent: { color: '#475467', backgroundColor: '#F2F4F7' },
};

const formatDate = (value, includeTime = false) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en', {
    day: '2-digit', month: 'short', year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
};

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

export const getCompanyReportPath = ({ companyId, tenantId }, isAdmin, isTenantDashboard) => {
  if (!companyId) return null;
  if (isAdmin && (isTenantDashboard || tenantId)) {
    return `/tenant-dashboard/${encodeURIComponent(tenantId)}/company/${encodeURIComponent(companyId)}/`;
  }
  return `/company/${encodeURIComponent(companyId)}`;
};

const StatusChip = ({ status, onClick }) => (
  <Chip
    label={status || 'Sent'}
    size="small"
    clickable={Boolean(onClick)}
    onClick={onClick}
    aria-label={onClick ? `Change status from ${status || 'Sent'}` : undefined}
    sx={{
    ...STATUS_STYLES[status || 'Sent'], minWidth: 82, height: 26, borderRadius: '7px',
    fontSize: 12, fontWeight: 700, '& .MuiChip-label': { px: 1.25 },
    ...(onClick ? {
      cursor: 'pointer',
      boxShadow: 'inset 0 0 0 1px rgba(16,42,86,.08)',
      '&:hover': { filter: 'brightness(.96)', boxShadow: 'inset 0 0 0 1px rgba(16,42,86,.2)' },
      '&:focus-visible': { outline: '2px solid #246BDB', outlineOffset: 2 },
    } : {}),
  }} />
);

const SummaryCard = ({ label, value, icon, tone }) => (
  <Card variant="outlined" sx={{ height: '100%', borderColor: '#E4EAF2', borderRadius: 2.5, boxShadow: '0 4px 16px rgba(18,38,63,.035)' }}>
    <CardContent sx={{ p: '18px !important', display: 'flex', alignItems: 'center', gap: 1.75 }}>
      <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', color: tone.color, bgcolor: tone.bg }}>{icon}</Box>
      <Box>
        <Typography variant="body2" sx={{ color: '#667085', fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ color: '#102A56', fontSize: 24, lineHeight: 1.2, fontWeight: 800 }}>{value}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const RequestToolbar = ({ search, onSearchChange, statusFilter, onStatusFilterChange }) => (
  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E7ECF3', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
    <TextField value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Search company or comment" size="small"
      sx={{ width: { xs: '100%', md: 360 }, mr: { md: 'auto' }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
    <GridToolbarFilterButton startIcon={<TuneIcon />} />
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <Select value={statusFilter} onChange={(event) => onStatusFilterChange(event.target.value)} sx={{ borderRadius: 2 }}>
        <MenuItem value="All">Status: All</MenuItem>
        {STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
      </Select>
    </FormControl>
    <GridToolbarColumnsButton />
    <GridToolbarExport />
  </Box>
);

export default function RequestStatus() {
  const location = useLocation();
  const navigate = useNavigate();
  const URLTenant = getTenantIdFromURL();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenantId = useSelector((state) => state.userInfo.tenantId);
  const adminEmail = useSelector((state) => state.userInfo.email);
  const isTenantDashboard = location.pathname.includes('/tenant-dashboard/');
  const [tenantId, setTenantId] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [statusDialog, setStatusDialog] = useState(null);
  const [replyDialog, setReplyDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentTenantId && !URLTenant && !isAdmin) return;
    if (isAdmin && isTenantDashboard) setTenantId(URLTenant);
    else if (isAdmin && !isTenantDashboard) setTenantId(null);
    else setTenantId(currentTenantId);
  }, [isAdmin, isTenantDashboard, currentTenantId, URLTenant]);

  const loadRequests = useCallback(async () => {
    if (tenantId === undefined) return;
    try {
      setLoading(true);
      const response = tenantId
        ? await requestStatusService.getRequestStatusesByTenantId(tenantId)
        : await requestStatusService.getAllRequestStatuses();
      const data = Array.isArray(response?.data) ? response.data : [];
      setRows(data.map((item) => ({
        id: item.id, companyId: item.companyId, name: item.companyName || 'Unnamed company',
        tenantName: item.tenantName, status: item.requestStatus || 'Sent',
        date: item.requestDate ? new Date(item.requestDate) : null,
        statusChangeDate: item.statusChangeDate ? new Date(item.statusChangeDate) : null,
        comment: item.comments || '', email: item.creatorEmail || item.email || '', phone: item.phoneNo || '',
        requestType: item.requestType || 'Manual Evaluation', tenantId: item.tenantId,
        crediarcReply: item.crediarcReply || '',
        replyUpdatedAt: item.replyUpdatedAt ? new Date(item.replyUpdatedAt) : null,
        replyBy: item.replyBy || '',
        lastNotificationAt: item.lastNotificationAt ? new Date(item.lastNotificationAt) : null,
      })));
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error(errorMessage(error, 'Failed to fetch requests'));
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const summary = useMemo(() => ({
    total: rows.length,
    awaiting: rows.filter((row) => row.status === 'Sent').length,
    processing: rows.filter((row) => row.status === 'Processing').length,
    completed: rows.filter((row) => row.status === 'Approved' || row.status === 'Rejected').length,
  }), [rows]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesStatus = statusFilter === 'All' || row.status === statusFilter;
      const matchesSearch = !needle || [row.name, row.tenantName, row.comment, row.crediarcReply, row.email]
        .some((value) => String(value || '').toLowerCase().includes(needle));
      return matchesStatus && matchesSearch;
    });
  }, [rows, search, statusFilter]);

  const openCompanyReport = useCallback((row) => {
    const path = getCompanyReportPath(row, isAdmin, isTenantDashboard);
    if (path) navigate(path);
  }, [isAdmin, isTenantDashboard, navigate]);

  const saveStatus = async () => {
    if (!statusDialog) return;
    const statusChanged = statusDialog.status !== statusDialog.row.status;
    const creatorEmail = String(statusDialog.email || '').trim().toLowerCase();
    const originalEmail = String(statusDialog.row.email || '').trim().toLowerCase();
    const emailChanged = creatorEmail !== originalEmail;
    if (emailChanged && !EMAIL_PATTERN.test(creatorEmail)) {
      toast.error('Enter a valid request creator email address');
      return;
    }
    try {
      setSaving(true);
      const updateData = {
        requestStatus: statusDialog.status,
        notifyCreator: (statusChanged || emailChanged) && statusDialog.notifyCreator,
      };
      if (statusChanged) updateData.statusChangeDate = new Date().toISOString();
      if (emailChanged) updateData.creatorEmail = creatorEmail;
      const response = await requestStatusService.updateRequestStatus(statusDialog.row.id, updateData);
      setStatusDialog(null);
      await loadRequests();
      if (response?.notification?.sent) toast.success(`Status updated and email sent to ${response.notification.recipient}`);
      else if (response?.notification?.error) toast.warning(`Status updated, but email failed: ${response.notification.error}`);
      else toast.success('Request status updated');
    } catch (error) { toast.error(errorMessage(error, 'Failed to update request status')); }
    finally { setSaving(false); }
  };

  const saveReply = async () => {
    const reply = replyDialog?.reply?.trim();
    if (!reply) return;
    try {
      setSaving(true);
      await requestStatusService.updateRequestStatus(replyDialog.row.id, {
        crediarcReply: reply, replyUpdatedAt: new Date().toISOString(), replyBy: adminEmail || 'CrediArc',
      });
      setReplyDialog(null);
      await loadRequests();
      toast.success('Reply saved');
    } catch (error) { toast.error(errorMessage(error, 'Failed to save reply')); }
    finally { setSaving(false); }
  };

  const sendNotification = useCallback(async (row) => {
    if (!row.email) return toast.warning('This request does not include a creator email address');
    if (!NOTIFIABLE_STATUSES.has(row.status)) return toast.warning('Email can be sent only for Processing, Approved, or Rejected');
    try {
      await requestStatusService.notifyRequestStatusCreator(row.id);
      toast.success(`Notification email sent to ${row.email}`);
      await loadRequests();
    } catch (error) { toast.error(errorMessage(error, 'Failed to send notification email')); }
  }, [loadRequests]);

  const columns = useMemo(() => {
    const result = [
      { field: 'name', headerName: 'Company', minWidth: 190, flex: 1.15,
        renderCell: ({ row }) => <Tooltip title="Open company report" arrow><Button variant="text" onClick={() => openCompanyReport(row)} disabled={!row.companyId}
          sx={{ p: 0, justifyContent: 'flex-start', color: '#102A56', fontWeight: 750, textTransform: 'none', textAlign: 'left' }}>{row.name}</Button></Tooltip> },
      { field: 'status', headerName: 'Status', minWidth: 115,
        renderCell: ({ row, value }) => isAdmin
          ? <Tooltip title="Change status" arrow><StatusChip status={value} onClick={() => setStatusDialog({ row, status: row.status, email: row.email || '', notifyCreator: false })} /></Tooltip>
          : <StatusChip status={value} /> },
      { field: 'date', headerName: 'Received', minWidth: 125, valueFormatter: ({ value }) => formatDate(value) },
      { field: 'comment', headerName: 'Customer Comment', minWidth: 220, flex: 1.4,
        renderCell: ({ value }) => <Tooltip title={value || 'No comment'} arrow><Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.35, py: 1.1 }}>{value || '—'}</Typography></Tooltip> },
      { field: 'crediarcReply', headerName: 'CrediArc Reply', minWidth: 230, flex: 1.45,
        renderCell: ({ row }) => row.crediarcReply ? <Tooltip title={row.crediarcReply} arrow><Box sx={{ py: .8 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>{row.crediarcReply}</Typography>
          <Typography variant="caption" sx={{ color: '#667085', display: 'flex', alignItems: 'center', gap: .5, mt: .25 }}><CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#12A66A' }} /> {formatDate(row.replyUpdatedAt, true)}</Typography>
        </Box></Tooltip> : isAdmin ? <Button size="small" variant="outlined" startIcon={<EditNoteOutlinedIcon />} onClick={() => setReplyDialog({ row, reply: '' })} sx={{ textTransform: 'none', borderRadius: 1.5 }}>Write reply</Button>
          : <Typography variant="body2" color="text.secondary">Awaiting reply</Typography> },
      { field: 'contact', headerName: 'Contact', minWidth: 210, flex: 1.25, sortable: false,
        renderCell: ({ row }) => <Box sx={{ py: .7, minWidth: 0 }}><Typography variant="body2" noWrap title={row.email}>{row.email || '—'}</Typography>{row.phone && <Typography variant="caption" color="text.secondary">{row.phone}</Typography>}</Box> },
      { field: 'requestType', headerName: 'Request Type', minWidth: 150, renderCell: ({ value }) => <Typography variant="body2" color="text.secondary">{value}</Typography> },
    ];
    if (isAdmin) result.push({
      field: 'actions', headerName: 'Actions', type: 'actions', width: 90, align: 'center', headerAlign: 'center',
      getActions: ({ row }) => [
        <GridActionsCellItem key="status" icon={<PendingActionsOutlinedIcon />} label="Update status" showInMenu onClick={() => setStatusDialog({ row, status: row.status, email: row.email || '', notifyCreator: false })} />,
        <GridActionsCellItem key="reply" icon={<EditNoteOutlinedIcon />} label="Write / edit reply" showInMenu onClick={() => setReplyDialog({ row, reply: row.crediarcReply || '' })} />,
        <GridActionsCellItem key="email" icon={<EmailOutlinedIcon color="primary" />} label="Send notification email" showInMenu
          disabled={!row.email || !NOTIFIABLE_STATUSES.has(row.status)} onClick={() => sendNotification(row)} />,
      ],
    });
    return result;
  }, [isAdmin, openCompanyReport, sendNotification]);

  return <Box sx={{ width: '100%', pb: 3 }}>
    <Box sx={{ mb: 2.5 }}>
      <Typography component="h1" sx={{ color: '#102A56', fontSize: { xs: 25, md: 30 }, fontWeight: 800, letterSpacing: '-.02em' }}>Manual Requests</Typography>
      <Typography color="text.secondary">Review, respond to and manage customer evaluation requests</Typography>
    </Box>
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6} lg={3}><SummaryCard label="Total requests" value={summary.total} icon={<ApartmentOutlinedIcon />} tone={{ color: '#246BDB', bg: '#EAF2FF' }} /></Grid>
      <Grid item xs={12} sm={6} lg={3}><SummaryCard label="Awaiting review" value={summary.awaiting} icon={<PendingActionsOutlinedIcon />} tone={{ color: '#B7791F', bg: '#FFF6DF' }} /></Grid>
      <Grid item xs={12} sm={6} lg={3}><SummaryCard label="Processing" value={summary.processing} icon={<TuneIcon />} tone={{ color: '#246BDB', bg: '#EAF2FF' }} /></Grid>
      <Grid item xs={12} sm={6} lg={3}><SummaryCard label="Completed" value={summary.completed} icon={<CheckCircleOutlineIcon />} tone={{ color: '#138A57', bg: '#E7F7EF' }} /></Grid>
    </Grid>
    <Box sx={{ bgcolor: '#fff', border: '1px solid #E4EAF2', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 8px 28px rgba(18,38,63,.05)' }}>
      <DataGridPremium rows={filteredRows} columns={columns} loading={loading} autoHeight getRowHeight={() => 'auto'} disableRowSelectionOnClick
        slots={{ toolbar: RequestToolbar, moreActionsIcon: MoreHorizIcon }}
        slotProps={{ toolbar: { search, onSearchChange: setSearch, statusFilter, onStatusFilterChange: setStatusFilter } }}
        initialState={{ sorting: { sortModel: [{ field: 'date', sort: 'desc' }] }, pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
        pageSizeOptions={[10, 25, 50]} sx={{ border: 0, color: '#243B5A',
          '& .MuiDataGrid-columnHeaders': { bgcolor: '#F8FAFD', borderBottom: '1px solid #E4EAF2', color: '#344054', fontWeight: 800 },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 750 }, '& .MuiDataGrid-cell': { borderColor: '#EEF1F6', py: .5, minHeight: 58, display: 'flex', alignItems: 'center' },
          '& .MuiDataGrid-row:hover': { bgcolor: '#F8FBFF' }, '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E4EAF2' } }} />
    </Box>

    <Dialog open={Boolean(statusDialog)} onClose={() => !saving && setStatusDialog(null)} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>Update request status</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Box><Typography variant="caption" color="text.secondary">Company</Typography><Typography fontWeight={700}>{statusDialog?.row?.name}</Typography></Box>
        <FormControl fullWidth><Select value={statusDialog?.status || 'Sent'} onChange={(event) => setStatusDialog((current) => ({ ...current, status: event.target.value }))}>
          {STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}</Select></FormControl>
        <TextField label="Request creator email" type="email" value={statusDialog?.email || ''}
          onChange={(event) => setStatusDialog((current) => ({ ...current, email: event.target.value, notifyCreator: false }))}
          error={Boolean(statusDialog?.email) && !EMAIL_PATTERN.test(String(statusDialog.email).trim())}
          helperText={statusDialog?.row?.email ? 'Notification recipient' : 'Enter the recipient for this legacy request'} />
        <FormControlLabel control={<Checkbox checked={Boolean(statusDialog?.notifyCreator)} onChange={(event) => setStatusDialog((current) => ({ ...current, notifyCreator: event.target.checked }))} />}
          label={statusDialog?.status === statusDialog?.row?.status ? 'Email the request creator about the current status' : 'Email the request creator about this status change'}
          disabled={!EMAIL_PATTERN.test(String(statusDialog?.email || '').trim()) || !NOTIFIABLE_STATUSES.has(statusDialog?.status)
            || (statusDialog?.status === statusDialog?.row?.status && String(statusDialog?.email || '').trim().toLowerCase() === String(statusDialog?.row?.email || '').trim().toLowerCase())} />
        {!statusDialog?.row?.email && <Typography variant="caption" color="warning.main">This legacy request has no stored creator. Enter and save the verified email above.</Typography>}
      </Stack></DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}><Button onClick={() => setStatusDialog(null)} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={saveStatus} disabled={saving || (statusDialog?.status === statusDialog?.row?.status
          && String(statusDialog?.email || '').trim().toLowerCase() === String(statusDialog?.row?.email || '').trim().toLowerCase())}>Save changes</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(replyDialog)} onClose={() => !saving && setReplyDialog(null)} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>{replyDialog?.row?.crediarcReply ? 'Edit CrediArc reply' : 'Write CrediArc reply'}</DialogTitle>
      <DialogContent><Stack spacing={1.5} sx={{ pt: 1 }}>
        <Box sx={{ p: 1.5, bgcolor: '#F7F9FC', borderRadius: 2 }}><Typography variant="caption" color="text.secondary">Customer comment</Typography><Typography variant="body2">{replyDialog?.row?.comment || 'No comment provided.'}</Typography></Box>
        <TextField autoFocus multiline minRows={5} label="Reply to comment" placeholder="Write CrediArc's response…" value={replyDialog?.reply || ''}
          onChange={(event) => setReplyDialog((current) => ({ ...current, reply: event.target.value }))} inputProps={{ maxLength: 2000 }} helperText={`${replyDialog?.reply?.length || 0}/2000`} />
      </Stack></DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}><Button onClick={() => setReplyDialog(null)} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={saveReply} disabled={saving || !replyDialog?.reply?.trim()}>Save reply</Button></DialogActions>
    </Dialog>
  </Box>;
}
